import React, { useState, useEffect } from 'react';
import AppShell from '../components/AppShell';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Separator } from '../components/ui/separator';
import { Download, RefreshCw, MessageSquare, User, Calendar, Hash } from 'lucide-react';
import { formatDate, getInitials } from '../lib/utils';
import api from '../lib/api';
import { toast } from 'sonner';

export default function SlackDebugPage() {
  const [messages, setMessages] = useState([]);
  const [sources, setSources] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  
  // Filters
  const [selectedSource, setSelectedSource] = useState('all');
  const [selectedProject, setSelectedProject] = useState('all');
  const [channelId, setChannelId] = useState('');
  const [limit, setLimit] = useState(100);

  useEffect(() => {
    loadSources();
    loadProjects();
    loadMessages();
  }, [selectedSource, selectedProject]);

  const loadSources = async () => {
    try {
      const res = await api.get('/sources');
      const slackSources = res.data.filter(s => s.source_type === 'slack');
      setSources(slackSources);
    } catch (error) {
      console.error('Failed to load sources:', error);
      toast.error('Failed to load sources');
    }
  };

  const loadProjects = async () => {
    try {
      const res = await api.get('/projects');
      setProjects(res.data);
    } catch (error) {
      console.error('Failed to load projects:', error);
    }
  };

  const loadMessages = async () => {
    try {
      setLoading(true);
      const params = {};
      if (selectedSource && selectedSource !== 'all') params.source_conn_id = selectedSource;
      if (selectedProject) params.project_id = selectedProject;
      params.limit = limit;

      const res = await api.get('/debug/slack-messages', { params });
      setMessages(res.data.messages || []);
    } catch (error) {
      console.error('Failed to load messages:', error);
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const syncChannel = async () => {
    if (!selectedSource || selectedSource === 'all' || !channelId) {
      toast.error('Please select a source and enter a channel ID');
      return;
    }

    try {
      setSyncing(true);
      const res = await api.post(
        `/integrations/slack/${selectedSource}/channels/${channelId}/sync`,
        {},
        { params: { limit } }
      );
      
      toast.success(`Synced ${res.data.created} messages (skipped ${res.data.skipped})`);
      loadMessages(); // Reload messages
    } catch (error) {
      console.error('Failed to sync channel:', error);
      toast.error(error.response?.data?.detail || 'Failed to sync channel');
    } finally {
      setSyncing(false);
    }
  };

  const exportMessages = () => {
    const dataStr = JSON.stringify(messages, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `slack-messages-${new Date().toISOString()}.json`;
    link.click();
  };

  return (
    <AppShell>
      <div className="container mx-auto p-6 space-y-6">
        <div>
          <h1 className="text-3xl font-bold mb-2">Slack Messages Debug</h1>
          <p className="text-muted-foreground">
            View and sync Slack messages stored in the database
          </p>
        </div>

        {/* Sync Section */}
        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-4">Sync Messages from Channel</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Slack Source</Label>
              <Select value={selectedSource || 'all'} onValueChange={setSelectedSource}>
                <SelectTrigger>
                  <SelectValue placeholder="Select source" />
                </SelectTrigger>
                <SelectContent>
                  {sources.map(source => (
                    <SelectItem key={source.id} value={source.id}>
                      {source.metadata?.workspace_name || source.source_type} ({source.connection_id})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Channel ID</Label>
              <Input
                placeholder="C1234567890"
                value={channelId}
                onChange={(e) => setChannelId(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>Limit</Label>
              <Input
                type="number"
                placeholder="100"
                value={limit}
                onChange={(e) => setLimit(parseInt(e.target.value) || 100)}
              />
            </div>

            <div className="space-y-2">
              <Label>&nbsp;</Label>
              <Button
                onClick={syncChannel}
                disabled={syncing || !selectedSource || selectedSource === 'all' || !channelId}
                className="w-full"
              >
                {syncing ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Syncing...
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4 mr-2" />
                    Sync Channel
                  </>
                )}
              </Button>
            </div>
          </div>
        </Card>

        {/* Filters */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">Filters</h2>
            <div className="flex gap-2">
              <Button variant="outline" onClick={loadMessages}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
              <Button variant="outline" onClick={exportMessages} disabled={messages.length === 0}>
                <Download className="h-4 w-4 mr-2" />
                Export JSON
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Filter by Source</Label>
              <Select value={selectedSource || 'all'} onValueChange={setSelectedSource}>
                <SelectTrigger>
                  <SelectValue placeholder="All sources" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All sources</SelectItem>
                  {sources.map(source => (
                    <SelectItem key={source.id} value={source.id}>
                      {source.metadata?.workspace_name || source.source_type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Filter by Project</Label>
              <Select value={selectedProject || 'all'} onValueChange={setSelectedProject}>
                <SelectTrigger>
                  <SelectValue placeholder="All projects" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All projects</SelectItem>
                  {projects.map(project => (
                    <SelectItem key={project.id} value={project.id}>
                      {project.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </Card>

        {/* Messages List */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">
              Messages ({messages.length})
            </h2>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-2 text-muted-foreground" />
              <p className="text-muted-foreground">Loading messages...</p>
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center py-12 border border-dashed border-border rounded-lg">
              <MessageSquare className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground mb-2">No messages found</p>
              <p className="text-sm text-muted-foreground">
                Sync messages from a Slack channel to get started
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {messages.map((msg) => (
                <Card key={msg.id} className="p-4">
                  <div className="flex gap-4">
                    <Avatar>
                      <AvatarImage src={msg.author_avatar} />
                      <AvatarFallback>{getInitials(msg.author_name || 'U')}</AvatarFallback>
                    </Avatar>

                    <div className="flex-1 space-y-2">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <User className="h-4 w-4 text-muted-foreground" />
                            <span className="font-medium">{msg.author_name || 'Unknown'}</span>
                            <span className="text-muted-foreground">•</span>
                            <Calendar className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm text-muted-foreground">
                              {formatDate(msg.timestamp)}
                            </span>
                          </div>

                          {msg.metadata?.channel_id && (
                            <div className="flex items-center gap-2 mb-2">
                              <Hash className="h-3 w-3 text-muted-foreground" />
                              <Badge variant="secondary" className="text-xs">
                                Channel: {msg.metadata.channel_id}
                              </Badge>
                            </div>
                          )}

                          <h3 className="font-semibold mb-1">{msg.title}</h3>
                          {msg.content && (
                            <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                              {msg.content}
                            </p>
                          )}
                        </div>

                        <div className="flex flex-col gap-2">
                          <Badge variant={msg.status === 'unread' ? 'default' : 'secondary'}>
                            {msg.status}
                          </Badge>
                          {msg.metadata?.replies > 0 && (
                            <Badge variant="outline">
                              {msg.metadata.replies} replies
                            </Badge>
                          )}
                        </div>
                      </div>

                      {/* Metadata */}
                      {msg.metadata && (
                        <div className="pt-2 border-t">
                          <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                            <span>ID: {msg.external_id}</span>
                            {msg.metadata.slack_ts && (
                              <span>• TS: {msg.metadata.slack_ts}</span>
                            )}
                            {msg.metadata.user_id && (
                              <span>• User: {msg.metadata.user_id}</span>
                            )}
                            {msg.project_id && (
                              <span>• Project: {msg.project_id}</span>
                            )}
                          </div>
                          {msg.external_url && msg.external_url !== '#' && (
                            <a
                              href={msg.external_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-primary hover:underline mt-1 inline-block"
                            >
                              View in Slack →
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </Card>
      </div>
    </AppShell>
  );
}

